import { useEffect } from 'react';
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/modules/auth/hooks/useAuth';
import { requiresProfileCompletion } from '@/modules/auth/lib/profileCompletion';
import { isTenantPortalUser } from '@/modules/auth/lib/userMode';
import { AppShell } from '@/shared/layout/AppShell';

export const Route = createFileRoute('/dashboard')({
  component: DashboardLayout,
});

function DashboardLayout() {
  const { t } = useTranslation();
  const { user, isAuthLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthLoading && !user) {
      void navigate({ to: '/auth/login', replace: true });
      return;
    }
    if (!isAuthLoading && user && requiresProfileCompletion(user)) {
      void navigate({ to: '/complete-profile', replace: true });
      return;
    }
    if (!isAuthLoading && user && isTenantPortalUser(user)) {
      void navigate({ to: '/portal', replace: true });
    }
  }, [user, isAuthLoading, navigate]);

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user || isTenantPortalUser(user) || requiresProfileCompletion(user)) {
    return null;
  }

  return (
    <AppShell user={user}>
      <Outlet />
    </AppShell>
  );
}
